import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const HeroAnimation = () => {
  const [phase, setPhase] = useState('idle'); // idle, typing, processing, result
  const [displayedText, setDisplayedText] = useState('');
  
  const messyInput = "אממ... שילמתי משהו כמו 85 שקל, רגע בעצם 80 על פיצה, וגם היה הנחה של 10 אחוז על מזומן";
  
  const resultLines = [
    { emoji: "🏷️", label: "כותרת", value: "פיצה" },
    { emoji: "🍕", label: "קטגוריה", value: "אוכל" },
    { emoji: "💵", label: "סכום", value: "72" },
    { emoji: "💳", label: "תשלום", value: "מזומן" },
  ];

  // Animation cycle
  useEffect(() => {
    let timeout;
    
    if (phase === 'idle') {
      timeout = setTimeout(() => {
        setPhase('typing');
        setDisplayedText('');
      }, 1000);
    } else if (phase === 'typing') {
      if (displayedText.length < messyInput.length) {
        timeout = setTimeout(() => {
          setDisplayedText(messyInput.slice(0, displayedText.length + 1));
        }, 50);
      } else {
        timeout = setTimeout(() => setPhase('processing'), 800);
      }
    } else if (phase === 'processing') {
      timeout = setTimeout(() => setPhase('result'), 1200);
    } else if (phase === 'result') {
      timeout = setTimeout(() => {
        setPhase('idle');
        setDisplayedText('');
      }, 7000);
    }
    
    return () => clearTimeout(timeout);
  }, [phase, displayedText]);

  // Generate stable random heights for waveform bars
  const barHeights = useMemo(() => 
    [...Array(28)].map(() => 12 + Math.random() * 20), 
  []);

  // Waveform active during typing phase
  const isTyping = phase === 'typing';

  return (
    <div className="w-full h-full min-h-[420px] flex items-center justify-center bg-background-light overflow-hidden font-sans rounded-3xl border-4 border-black p-6">
      <div className="w-full max-w-md flex flex-col items-center gap-5">
        
        {/* Recording Input Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full bg-pink-300 rounded-2xl p-5 shadow-hard border-4 border-black"
        >
          {/* Waveform */}
          <div className="flex items-center justify-center gap-[3px] h-10 mb-2">
            {barHeights.map((maxHeight, i) => (
              <motion.div
                key={i}
                className="w-[3px] bg-pink-600 rounded-full"
                animate={isTyping ? {
                  height: [6, maxHeight, 6],
                } : { height: 6 }}
                transition={{
                  duration: 0.4 + (i % 3) * 0.1,
                  repeat: isTyping ? Infinity : 0,
                  delay: i * 0.015,
                  ease: "easeInOut",
                  repeatType: "loop"
                }}
              />
            ))}
          </div>
          
          {/* Recording indicator - always visible */}
          <div className="text-center mb-2">
            <span className="text-gray-700 text-sm font-bold">🎤 מקליט...</span>
          </div>
          
          {/* Messy transcription text */}
          <div className="min-h-[60px] flex items-center justify-center" dir="rtl">
            <AnimatePresence mode="wait">
              {(phase === 'typing' || phase === 'processing' || phase === 'result') && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-black text-lg font-medium text-center leading-relaxed"
                >
                  {displayedText}
                  {phase === 'typing' && (
                    <motion.span
                      animate={{ opacity: [1, 0] }}
                      transition={{ duration: 0.5, repeat: Infinity }}
                      className="inline-block w-[2px] h-5 bg-pink-600 mr-1 align-middle"
                    />
                  )}
                </motion.p>
              )}
              {phase === 'idle' && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.5 }}
                  className="text-gray-500 text-base font-medium"
                >
                  ...
                </motion.p>
              )}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Output Card Container - Fixed height to prevent layout shift */}
        <div className="w-full h-[260px]">
          {/* Processing indicator */}
          <AnimatePresence>
            {phase === 'processing' && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="flex items-center justify-center gap-2 py-2 h-full"
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
                  className="w-5 h-5 border-3 border-primary border-t-transparent rounded-full"
                />
                <span className="text-gray-700 font-bold text-sm">✨ מנתח...</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Clean Output Card - Line by Line */}
          <AnimatePresence>
            {phase === 'result' && (
              <motion.div
                initial={{ opacity: 0, y: 30, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ type: "spring", bounce: 0.4 }}
                className="w-full bg-white rounded-2xl border-4 border-black shadow-hard overflow-hidden"
              >
                {/* Header */}
                <div className="bg-green-400 border-b-4 border-black px-4 py-2">
                  <p className="font-black text-center text-black">הוצאה נוספה!</p>
                </div>
                {/* Result Lines */}
                <div className="p-4 space-y-2" dir="rtl">
                  {resultLines.map((line, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 + i * 0.1 }}
                      className="flex items-center justify-between bg-gray-50 border-2 border-black rounded-lg px-4 py-2"
                    >
                      <span className="font-black text-sm">{line.emoji} {line.label}</span>
                      <span className="font-bold text-sm">{line.value}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

      </div>
    </div>
  );
};

export default HeroAnimation;
